import { CONTACT_LEAD_API } from '../config/contactConfig';
import { FEATURES } from '../config/features';
import { getUserIdentifier } from './identity';

// Same "From" convention as the chat / flight / bus flows.
// The Lambda derives user_id with From.split(":")[-1].
const fromField = () => `webbot_new:${getUserIdentifier()}`;

const post = async (intent, payload, { keepalive = false, timeoutMs = 0 } = {}) => {
    const controller = timeoutMs ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
    try {
        const res = await fetch(CONTACT_LEAD_API, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            // keepalive lets the lead update finish even if the page navigates to tel:
            keepalive,
            signal: controller?.signal,
            body: JSON.stringify({
                ...payload,
                intent,
                From: fromField(),
            }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json().catch(() => ({}));
        if (json && json.status && Number(json.status) !== 200) {
            throw new Error(json.message || `status ${json.status}`);
        }
        return json?.data ?? null;
    } finally {
        if (timer) clearTimeout(timer);
    }
};

/**
 * intent "contact_lead_config" — called once per route search.
 * Returns the backend config, or null (→ treat as disabled) on any failure.
 */
export const fetchContactLeadConfig = async () => {
    if (!FEATURES.ENABLE_CONTACT_FORM) return null;
    try {
        const data = await post('contact_lead_config', {}, { timeoutMs: 8000 });
        return data && typeof data === 'object' ? data : null;
    } catch (e) {
        console.error('[ContactLead] config fetch failed', e);
        return null;
    }
};

export const newLeadId = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return `RCLL-${crypto.randomUUID()}`;
    }
    return `RCLL-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

/**
 * Creates the lead row (backend sets lead_status = CLICKED and sends the SMS alert).
 * Never throws — logging must not break the UI.
 */
export const createLead = async (lead) => {
    if (!FEATURES.ENABLE_CONTACT_FORM) return null;
    try {
        return await post('contact_lead', { ...lead, action: 'create' }, { keepalive: true });
    } catch (e) {
        console.error('[ContactLead] create failed', e);
        return null;
    }
};

/**
 * Updates the same row with what the user did in the modal.
 * leadAction: 'call_us' | 'callback_request'
 * Returns true on success so the modal can show an error for callbacks.
 */
export const updateLead = async (leadId, leadAction, fields) => {
    if (!FEATURES.ENABLE_CONTACT_FORM || !leadId) return false;
    try {
        await post('contact_lead', {
            ...fields,
            action: 'update',
            lead_id: leadId,
            lead_action: leadAction,
        }, { keepalive: true });
        return true;
    } catch (e) {
        console.error('[ContactLead] update failed', e);
        return false;
    }
};
